import { createRouter, createWebHashHistory, type Router } from 'vue-router';
import { computeViewer2HashRedirect } from '../utils/route-params';
import { getCurrentVersion, switchVersion } from '../utils/versions';

/**
 * 首页去 # 逻辑：当路由切到 / 时，用 replaceState 把 URL 中的 hash 去掉，
 * 让首页 URL 保持干净。其他路由保持正常的 #/xxx hash 模式。
 */
function setupHomeCleanUrl(router: Router) {
  const stripHash = () => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  router.afterEach((to) => {
    if (to.path === '/') stripHash();
  });

  // 首次加载：如果 hash 为空或为 #/，也清理
  if (!window.location.hash || window.location.hash === '#/' || window.location.hash === '#') {
    stripHash();
  }
}

/**
 * 根据当前页面 URL 推断 editor 子应用的基础路径。
 * 例如当前页面为 https://res.cymini.qq.com/team6-base/dev/gs-viewer2/index.html
 * 则 editor base 为 https://res.cymini.qq.com/team6-base/dev/gs-viewer2/editor
 */
function getEditorBaseUrl(): string {
  const { origin, pathname } = window.location;
  const dir = pathname.replace(/\/[^/]*$/, '');
  return `${origin}${dir}/editor`;
}

/**
 * 非 hash 模式 /viewer2 重定向：
 * 当用户直接访问 /viewer2?sceneUrl=xxx（无 #）时，
 * 用 replaceState 将 URL 改为 /#/viewer2?sceneUrl=xxx，避免页面刷新。
 * 必须在 createRouter 之前执行，确保路由初始化时读到正确的 hash。
 */
function redirectNonHashViewer2() {
  const { pathname, search, hash, origin } = window.location;
  const target = computeViewer2HashRedirect(pathname, search, hash);
  if (!target) return;
  window.history.replaceState(null, '', `${origin}${target}`);
}

redirectNonHashViewer2();

const routes = [
  {
    path: '/',
    name: 'ModelList',
    component: () => import('../pages/ModelList.vue'),
    meta: { title: 'Web Wide World' },
  },
  {
    path: '/viewer2/:segments(.*)*',

    name: 'viewer2',
    component: () => import('../pages/scene-viewer/SceneViewer.vue'),
    props: true,
  },

  {
    path: '/debug-viewer/:segments(.*)*',
    name: 'debugViewer',
    component: () => import('../pages/scene-viewer/DebugSceneViewer.vue'),
    meta: { title: 'Debug Scene Viewer' },
    props: true,
  },
  {
    path: '/creative/:segments(.*)*',
    name: 'creativeViewer',
    component: () => import('../pages/creative-viewer/CreativeViewer.vue'),
    meta: { title: 'Creative Mode Test' },
  },
  // Editor 子应用跳转：重定向到 COS 上独立部署的页面
  {
    path: '/editor/navigator/:pathMatch(.*)*',
    name: 'editorNavigator',
    beforeEnter: () => {
      window.location.href = `${getEditorBaseUrl()}/navigator/index.html`;
      return false;
    },
    component: { render: () => null },
  },
  {
    path: '/editor/splat-studio/:pathMatch(.*)*',
    name: 'editorSplatStudio',
    beforeEnter: () => {
      window.location.href = `${getEditorBaseUrl()}/splat-studio/index.html`;
      return false;
    },
    component: { render: () => null },
  },
  {
    path: '/editor/file-converter/:pathMatch(.*)*',
    name: 'editorFileConverter',
    beforeEnter: () => {
      window.location.href = `${getEditorBaseUrl()}/file-converter/index.html`;
      return false;
    },
    component: { render: () => null },
  },
  {
    path: '/editor/meta-editor/:pathMatch(.*)*',
    name: 'editorMetaEditor',
    beforeEnter: () => {
      window.location.href = `${getEditorBaseUrl()}/meta-editor/index.html`;
      return false;
    },
    component: { render: () => null },
  },
  {
    path: '/editor/map-editor/:pathMatch(.*)*',
    name: 'editorMapEditor',
    beforeEnter: () => {
      window.location.href = `${getEditorBaseUrl()}/map-editor/index.html`;
      return false;
    },
    component: { render: () => null },
  },
  {
    path: '/editor/character-editor/:pathMatch(.*)*',
    name: 'editorCharacterEditor',
    beforeEnter: () => {
      window.location.href = `${getEditorBaseUrl()}/character-editor/index.html`;
      return false;
    },
    component: { render: () => null },
  },
  // siteUri 入口：不带 /viewer2 前缀，放最后作为 catch-all
  // 仅在 URL 不以已知前缀开头时才命中（如 #/world://...site）
  {
    path: '/:segments(.*)*',
    name: 'siteScene',
    component: () => import('../pages/scene-viewer/SceneViewer.vue'),
    props: true,
    meta: { title: 'Web Wide World' },
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// 首页去 #：URL 保持干净
setupHomeCleanUrl(router);

router.beforeEach((to, from, next) => {
  if (to.meta.title) {
    // @ts-ignore
    document.title = to.meta.title;
  }

  // 非首页路由
  if (to.path !== '/') {
    const fromVersion = (from.query.version as string) || '';
    const toVersion = (to.query.version as string) || '';

    // 检测用户在地址栏编辑了 version（同路由 + version 值变化）
    if (from.path === to.path && fromVersion !== toVersion) {
      // 验证除 version 外其他 query 参数完全相同，排除"切换场景"等正常导航
      const fromRest = { ...from.query };
      const toRest = { ...to.query };
      delete fromRest.version;
      delete toRest.version;

      if (JSON.stringify(fromRest) === JSON.stringify(toRest)) {
        // 确认是用户在地址栏改了 version → 切换版本并 reload
        switchVersion(toVersion || null);
        return; // switchVersion 内部会 reload，不会走到 next
      }
    }

    // 原有逻辑：自动补充 version 到 query
    const v = getCurrentVersion();
    if (v && v !== 'latest' && !to.query.version) {
      next({ ...to, query: { ...to.query, version: v }, replace: false });
      return;
    }
  }
  next();
});
export default router;
